import { action, makeObservable, observable, runInAction } from 'mobx';
import { RootStore } from './root';
import { WithNetworkConcurrency } from './with-network-concurrency';
import { NotificationManager } from '../components/notification-manager';
import { HttpMethod } from '../types';
import { RppAssetType, UploadTarget } from '../utils/rpp-assets';
import { TEN_PER_PAGE } from '../utils/const';

export type RppAdStatus = 'draft' | 'active' | 'archived';

export const RPP_AD_STATUSES: RppAdStatus[] = ['draft', 'active', 'archived'];

/**
 * An RPP ad as returned by the backend. The detail/create/update responses
 * include signed preview URLs (`videoUrl`); list responses omit them.
 */
export interface RppAd {
    id: number;
    title: string;
    advertiser: string | null;
    videoKey: string;
    durationSeconds: number | null;
    status: RppAdStatus;
    startDate: string;
    endDate: string;
    provinceIds: number[];
    videoUrl: string | null;
}

/** Local form state for the create/edit screen. */
export interface RppAdForm {
    title: string;
    advertiser: string;
    videoKey: string;
    durationSeconds: number | null;
    status: RppAdStatus;
    startDate: string;
    endDate: string;
    provinceIds: number[];
}

/** Body sent to `POST /rpp-ads` (and, as a full body, to `PUT /rpp-ads/{id}`). */
export interface RppAdPayload {
    title: string;
    advertiser: string | null;
    videoKey: string;
    durationSeconds: number | null;
    status: RppAdStatus;
    startDate: string;
    endDate: string;
    provinceIds: number[];
}

export interface RppAdListResponse {
    page: number;
    perPage: number;
    total: number;
    results: RppAd[];
}

/** List query params, expressed in the CMS's 0-based page convention. */
export type RppAdSearchParams = {
    page?: number;
    perPage?: number;
};

const MAX_RPP_ADS_PER_PAGE = 100;

export function normalizeRppAdSearchParams(
    searchParams: RppAdSearchParams
): Required<RppAdSearchParams> {
    const page = Number(searchParams.page);
    const perPage = Number(searchParams.perPage);

    return {
        page: Number.isInteger(page) && page >= 0 ? page : 0,
        perPage:
            Number.isInteger(perPage) &&
            perPage > 0 &&
            perPage <= MAX_RPP_ADS_PER_PAGE
                ? perPage
                : TEN_PER_PAGE,
    };
}

export interface CreateUploadTargetRequest {
    assetType: RppAssetType;
    contentType: string;
    contentLength: number;
}

interface SignedUrlResponse {
    key: string;
    url: string;
    expiresAt: string;
}

export class RppAdStore extends WithNetworkConcurrency {
    @observable rppAds: RppAdListResponse | null = null;
    @observable isFetchingList = false;
    @observable isFetchingAd = false;
    @observable isSaving = false;
    @observable isDeleting = false;

    rootStore: RootStore;

    constructor(rootStore: RootStore) {
        super();
        makeObservable(this);
        this.rootStore = rootStore;
    }

    @action
    async fetchRppAdList(
        this: RppAdStore,
        searchParams: RppAdSearchParams = {}
    ) {
        const tag = this.getTag();
        const normalizedSearchParams = normalizeRppAdSearchParams(searchParams);
        this.isFetchingList = true;
        try {
            const response = await this.rootStore.makeNetworkCall<
                RppAdListResponse
            >({
                method: HttpMethod.GET,
                url: '/rpp-ads',
                params: {
                    page: normalizedSearchParams.page + 1,
                    perPage: normalizedSearchParams.perPage,
                },
            });

            if (!response.err && this.isLatestTag(tag)) {
                runInAction(() => {
                    this.rppAds = response.data;
                });
            }
        } finally {
            if (this.isLatestTag(tag)) {
                runInAction(() => {
                    this.isFetchingList = false;
                });
            }
        }
    }

    @action
    async fetchRppAd(this: RppAdStore, id: string): Promise<RppAd | null> {
        this.isFetchingAd = true;
        try {
            const response = await this.rootStore.makeNetworkCall<RppAd>({
                method: HttpMethod.GET,
                url: `/rpp-ads/${id}`,
            });

            return !response.err && response.data ? response.data : null;
        } finally {
            this.isFetchingAd = false;
        }
    }

    @action
    async createRppAd(
        this: RppAdStore,
        data: RppAdPayload
    ): Promise<RppAd | null> {
        this.isSaving = true;
        const response = await this.rootStore.makeNetworkCall<RppAd>({
            method: HttpMethod.POST,
            url: '/rpp-ads',
            data,
        });

        if (!response.err) {
            NotificationManager.showSuccess('RPP ad created.');
        }

        this.isSaving = false;
        return !response.err && response.data ? response.data : null;
    }

    @action
    async updateRppAd(
        this: RppAdStore,
        id: number,
        data: RppAdPayload
    ): Promise<RppAd | null> {
        this.isSaving = true;
        const response = await this.rootStore.makeNetworkCall<RppAd>({
            method: HttpMethod.PUT,
            url: `/rpp-ads/${id}`,
            data,
        });

        if (!response.err) {
            NotificationManager.showSuccess('RPP ad updated.');
        }

        this.isSaving = false;
        return !response.err && response.data ? response.data : null;
    }

    @action
    async deleteRppAd(this: RppAdStore, id: number) {
        this.isDeleting = true;
        const response = await this.rootStore.makeNetworkCall({
            method: HttpMethod.DELETE,
            url: `/rpp-ads/${id}`,
        });

        if (!response.err) {
            NotificationManager.showSuccess('RPP ad deleted.');
        }

        this.isDeleting = false;
        return response;
    }

    /** Request a presigned S3 POST target. Returns null on error. */
    @action
    async createUploadTarget(
        this: RppAdStore,
        request: CreateUploadTargetRequest
    ): Promise<UploadTarget | null> {
        const response = await this.rootStore.makeNetworkCall<UploadTarget>({
            method: HttpMethod.POST,
            url: '/rpp-assets/upload-target',
            data: request,
        });

        return response.err ? null : response.data;
    }

    /** Get a short-lived signed URL to preview an uploaded asset. */
    @action
    async getSignedUrl(this: RppAdStore, key: string): Promise<string | null> {
        const response = await this.rootStore.makeNetworkCall<
            SignedUrlResponse
        >({
            method: HttpMethod.POST,
            url: '/rpp-assets/signed-url',
            data: { key },
        });

        return response.err || !response.data ? null : response.data.url;
    }
}
